import {
  useDeleteTenantLogo,
  useUploadTenantLogo,
} from '@/api/hooks/useTenantConfig'
import {
  CameraOutlined,
  DeleteOutlined,
  LoadingOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { Button, Modal, message } from 'antd'
import { useRef, useState } from 'react'

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB

interface TenantAvatarProps {
  tenantName: string
  logoUrl?: string | null
  logoThumbUrl?: string | null
}

export function TenantAvatar({
  tenantName,
  logoUrl,
  logoThumbUrl,
}: TenantAvatarProps) {
  // Prefer thumbnail URL for display, fall back to full URL
  const displayUrl = logoThumbUrl || logoUrl
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const uploadMutation = useUploadTenantLogo()
  const deleteMutation = useDeleteTenantLogo()

  const isLoading = uploadMutation.isPending || deleteMutation.isPending

  const handleAvatarClick = () => {
    if (isLoading) return

    if (displayUrl) {
      setModalOpen(true)
    } else {
      fileInputRef.current?.click()
    }
  }

  const handleUploadClick = () => {
    setModalOpen(false)
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    e.target.value = ''

    if (!ALLOWED_TYPES.includes(file.type)) {
      message.error(
        'Please select a valid image file (JPEG, PNG, GIF, or WebP)',
      )
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      message.error('File size must be less than 5MB')
      return
    }

    try {
      await uploadMutation.mutateAsync(file)
      message.success('Logo uploaded successfully')
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : 'Failed to upload logo',
      )
    }
  }

  const handleDelete = async () => {
    setModalOpen(false)
    try {
      await deleteMutation.mutateAsync()
      message.success('Logo deleted successfully')
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : 'Failed to delete logo',
      )
    }
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type='file'
        accept={ALLOWED_TYPES.join(',')}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <div
        onClick={handleAvatarClick}
        style={{
          width: 48,
          height: 48,
          borderRadius: 8,
          backgroundColor: displayUrl ? 'transparent' : '#f59e0b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: 20,
          cursor: isLoading ? 'wait' : 'pointer',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {isLoading ? (
          <LoadingOutlined
            style={{ fontSize: 20, color: displayUrl ? '#666' : 'white' }}
          />
        ) : displayUrl ? (
          <img
            src={displayUrl}
            alt={`${tenantName} logo`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              imageRendering: 'crisp-edges',
            }}
          />
        ) : (
          tenantName.charAt(0).toUpperCase()
        )}

        {!isLoading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              transition: 'opacity 0.2s',
              borderRadius: 8,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '1'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '0'
            }}
          >
            <CameraOutlined style={{ fontSize: 18, color: 'white' }} />
          </div>
        )}
      </div>

      <Modal
        title='Tenant Logo'
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={300}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Button icon={<UploadOutlined />} onClick={handleUploadClick} block>
            Upload New Logo
          </Button>
          <Button icon={<DeleteOutlined />} danger onClick={handleDelete} block>
            Delete Logo
          </Button>
        </div>
      </Modal>
    </>
  )
}
